import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/base-query';
import { getUserAccessToken } from 'utils/localstorage.helper';

// Define a service using a base URL and expected endpoints
export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery: baseQuery,
  tagTypes: ['SEARCHES'],
  endpoints: (builder) => ({
    findLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/find',
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      invalidatesTags: ['SEARCHES']
    }),
    lookupLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/leads/lookup`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      }
    }),
    getSearches: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/leads/searches',
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      providesTags: ['SEARCHES']
    }),
    getAllSavedSearches: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/leads/searches/saved',
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      }
    }),
    createSavedSearch: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/searches/saved',
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      invalidatesTags: ['SEARCHES']
    }),
    updateSearch: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `leads/searches/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      invalidatesTags: ['SEARCHES']
    }),
    deleteSearch: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `leads/searches/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      invalidatesTags: ['SEARCHES']
    }),
    addLeadsToCampaign: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `leads/add-to-campaign`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      }
    }),
    getLeads: builder.query({
      query: (id) => {
        return {
          method: 'GET',
          url: '/leads/' + id,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      providesTags: (result, error, arg) => [`LEADS_${arg}`]
    }),
    deleteLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'DELETE',
          url: '/leads',
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      }
    }),
    moveToCampaign: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'POST',
          url: `/leads/move-to-campaign/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      }
    }),
    updateLead: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/leads/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`
          }
        };
      },
      invalidatesTags: [`LEADS`]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFindLeadsMutation,
  useLookupLeadsMutation,
  useGetSearchesQuery,
  useLazyGetAllSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useUpdateSearchMutation,
  useDeleteSearchMutation,
  useAddLeadsToCampaignMutation,
  useGetLeadsQuery,
  useDeleteLeadsMutation,
  useMoveToCampaignMutation,
  useUpdateLeadMutation
} = leadsApi;
